import React from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  LineChart,
  TrendingUp,
  Users,
  Zap,
  Target,
  Brain,
  Activity,
  BarChart2,
  GitBranch
} from 'lucide-react';

const Analytics: React.FC = () => {
  return (
    <Layout currentPage="analytics">
      <div className="h-full overflow-y-auto custom-scrollbar">
        <div className="p-6">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground-primary mb-2 neon-blue">
              Análises
            </h1>
            <p className="text-foreground-tertiary">
              Insights inteligentes e análises preditivas dos seus projetos
            </p>
          </div>

          {/* Under Development Notice */}
          <Card variant="glass" className="w-full mb-8 border-2 border-purple-500/30">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-full flex items-center justify-center">
                <Brain className="w-10 h-10 text-purple-400 animate-pulse" />
              </div>
              <h2 className="text-2xl font-bold text-foreground-primary mb-3">
                Em Desenvolvimento
              </h2>
              <p className="text-foreground-secondary text-lg mb-2">
                A funcionalidade de Análises está sendo desenvolvida e estará disponível em breve.
              </p>
              <p className="text-foreground-tertiary">
                Estamos trabalhando em análises inteligentes e insights preditivos para seus projetos.
              </p>
            </CardContent>
          </Card>

          {/* Preview Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Trend Analysis Card */}
            <Card variant="glass" className="card-hover opacity-60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  Análise de Tendências
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground-tertiary text-sm">
                  Identifique padrões e tendências no desempenho dos projetos.
                </p>
              </CardContent>
            </Card>

            {/* Team Performance Card */}
            <Card variant="glass" className="card-hover opacity-60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="w-5 h-5 text-blue-400" />
                  Performance da Equipe
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground-tertiary text-sm">
                  Análise detalhada do desempenho individual e coletivo.
                </p>
              </CardContent>
            </Card>

            {/* Velocity Card */}
            <Card variant="glass" className="card-hover opacity-60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  Velocidade de Entrega
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground-tertiary text-sm">
                  Meça a velocidade de conclusão de tarefas e sprints.
                </p>
              </CardContent>
            </Card>

            {/* Predictive Analysis Card */}
            <Card variant="glass" className="card-hover opacity-60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Brain className="w-5 h-5 text-purple-400" />
                  Análise Preditiva
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground-tertiary text-sm">
                  Previsões baseadas em dados históricos e tendências.
                </p>
              </CardContent>
            </Card>

            {/* Goal Achievement Card */}
            <Card variant="glass" className="card-hover opacity-60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Target className="w-5 h-5 text-red-400" />
                  Alcance de Metas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground-tertiary text-sm">
                  Análise do progresso em relação às metas estabelecidas.
                </p>
              </CardContent>
            </Card>

            {/* Bottleneck Detection Card */}
            <Card variant="glass" className="card-hover opacity-60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Activity className="w-5 h-5 text-orange-400" />
                  Detecção de Gargalos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground-tertiary text-sm">
                  Identifique e resolva gargalos no fluxo de trabalho.
                </p>
              </CardContent>
            </Card>

            {/* Comparative Analysis Card */}
            <Card variant="glass" className="card-hover opacity-60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BarChart2 className="w-5 h-5 text-cyan-400" />
                  Análise Comparativa
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground-tertiary text-sm">
                  Compare performance entre projetos, equipes e períodos.
                </p>
              </CardContent>
            </Card>

            {/* Timeline Analysis Card */}
            <Card variant="glass" className="card-hover opacity-60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <LineChart className="w-5 h-5 text-green-400" />
                  Análise de Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground-tertiary text-sm">
                  Visualize a evolução temporal dos seus projetos.
                </p>
              </CardContent>
            </Card>

            {/* Dependency Analysis Card */}
            <Card variant="glass" className="card-hover opacity-60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <GitBranch className="w-5 h-5 text-pink-400" />
                  Análise de Dependências
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground-tertiary text-sm">
                  Mapeie e otimize dependências entre tarefas.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Additional Info */}
          <Card variant="glass" className="w-full mt-8">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Brain className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-foreground-primary mb-2">
                    O que esperar das Análises?
                  </h3>
                  <ul className="space-y-2 text-foreground-secondary">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-400 mt-1">•</span>
                      <span>Insights automáticos baseados em inteligência artificial</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-400 mt-1">•</span>
                      <span>Previsões de conclusão de projetos e identificação de riscos</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-400 mt-1">•</span>
                      <span>Recomendações personalizadas para otimização de processos</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-400 mt-1">•</span>
                      <span>Análise de padrões de trabalho e sugestões de melhorias</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-400 mt-1">•</span>
                      <span>Dashboards personalizáveis com métricas relevantes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-400 mt-1">•</span>
                      <span>Alertas inteligentes sobre desvios e anomalias</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Analytics;
